import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Marker } from 'react-mapbox-gl';

import { getStopsMapObject } from '../../store/selectors/ui/routeDetail';
import StopMarkerRequestPopup from '../StopMarkerRequestPopup';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import usePrevious from '../../hooks/usePrevious';
import { setStopMarkerPopup } from '../../store/reducers/map';
import {
  getMapBounds,
  getStopPopup,
  getZoomLevel,
  isOuterZoom,
} from '../../store/selectors/map';
import useCluster from './useClusters';
import ClusterMarker from '../Markers/ClusterMarker';
import ClusterPopup from '../ClusterPopup/ClusterPopup';
import MarkerWithClickHandler from './MarkerWithClickHandler';
import { MAP_POPUP_TYPES } from '../../constants/map';

const RouteStopsMapLayer = () => {
  const dispatch = useDispatch();
  const requestPopup = useSelector(getStopPopup);
  const isOuterZoomLevel = useSelector(isOuterZoom);
  const stopsMapObject = useSelector(getStopsMapObject);
  const zoom = useSelector(getZoomLevel);
  const bounds = useSelector(getMapBounds);
  const { routeId, detailVisible } = useLocationQueryParams();

  const { clusters, clusterRef } = useCluster({
    points: stopsMapObject,
    bounds,
    zoom,
  });

  const prevRouteId = usePrevious(routeId);

  useEffect(() => {
    if (requestPopup && (routeId !== prevRouteId || detailVisible)) {
      isOuterZoomLevel &&
        requestPopup.type === MAP_POPUP_TYPES.STOP &&
        dispatch(setStopMarkerPopup(null));
    }
  }, [
    routeId,
    prevRouteId,
    detailVisible,
    isOuterZoomLevel,
    requestPopup,
    dispatch,
  ]);

  const closePopup = useCallback(
    () => dispatch(setStopMarkerPopup(null)),
    [dispatch]
  );

  const isActiveCluster = useCallback(
    (feature) =>
      requestPopup &&
      requestPopup.type === MAP_POPUP_TYPES.CLUSTER &&
      requestPopup.stop.properties.cluster_id === feature.properties.cluster_id,
    [requestPopup]
  );

  return (
    <>
      {clusters.map((feature, index) =>
        feature.properties.cluster ? (
          <ClusterMarker
            key={index}
            feature={feature}
            clusterRef={clusterRef}
            isActive={isActiveCluster(feature)}
          />
        ) : (
          <MarkerWithClickHandler
            key={index}
            feature={feature}
            parentComponent={Marker}
          />
        )
      )}
      {requestPopup &&
        (requestPopup.type === MAP_POPUP_TYPES.STOP ? (
          <StopMarkerRequestPopup onClose={closePopup} {...requestPopup} />
        ) : (
          <ClusterPopup
            onClose={closePopup}
            clusterRef={clusterRef}
            {...requestPopup}
          />
        ))}
    </>
  );
};

export default memo(RouteStopsMapLayer);
